import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Send, Brain, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIAssistantPage = () => {
  const { getAuthHeader } = useAuth();
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [symptomInput, setSymptomInput] = useState('');
  const [symptomResult, setSymptomResult] = useState(null);
  const [progressAnalysis, setProgressAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [outcomeForm, setOutcomeForm] = useState({ outcome_type: 'sleep', rating: 3, notes: '' });
  const [outcomeSubmitting, setOutcomeSubmitting] = useState(false);

  useEffect(() => {
    fetchProgressAnalysis();
  }, []);

  const fetchProgressAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/ai/progress-analysis`, { headers: getAuthHeader() });
      setProgressAnalysis(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleChatSend = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages([...chatMessages, userMessage]);
    setChatInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai/chat`,
        null,
        { 
          params: { message: chatInput },
          headers: getAuthHeader() 
        }
      );
      const aiMessage = { role: 'assistant', content: response.data.response };
      setChatMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to get response');
    } finally {
      setLoading(false);
    }
  };

  const handleTrackOutcome = async (e) => {
    e.preventDefault();
    setOutcomeSubmitting(true);
    try {
      await axios.post(
        `${API}/ai/track-outcome`,
        null,
        {
          params: {
            outcome_type: outcomeForm.outcome_type,
            rating: outcomeForm.rating,
            notes: outcomeForm.notes || undefined,
          },
          headers: getAuthHeader(),
        }
      );
      toast.success('Outcome logged');
      setOutcomeForm({ outcome_type: 'sleep', rating: 3, notes: '' });
      fetchProgressAnalysis();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log outcome');
    } finally {
      setOutcomeSubmitting(false);
    }
  };

  const handleSymptomCheck = async (e) => {
    e.preventDefault();
    if (!symptomInput.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/ai/symptom-check`,
        null,
        { 
          params: { symptoms: symptomInput },
          headers: getAuthHeader() 
        }
      );
      setSymptomResult(response.data);
    } catch (error) {
      toast.error('Failed to analyze symptoms');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="ai-assistant-page">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            AI Wellness Assistant
          </h1>
          <p className="text-lg text-gray-600">Get personalized guidance powered by AI</p>
        </div>

        <Tabs defaultValue="chat" className="mb-8">
          <TabsList className="grid w-full grid-cols-3 max-w-2xl">
            <TabsTrigger value="chat" data-testid="chat-tab">AI Chat</TabsTrigger>
            <TabsTrigger value="symptoms" data-testid="symptoms-tab">Symptom Checker</TabsTrigger>
            <TabsTrigger value="progress" data-testid="progress-tab">Progress Tracker</TabsTrigger>
          </TabsList>

          {/* AI Chat */}
          <TabsContent value="chat">
            <Card className="max-w-4xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Brain className="h-6 w-6 text-primary" />
                  <span>Ayurveda Chat Assistant</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-4 h-96 overflow-y-auto">
                  {chatMessages.length === 0 && (
                    <div className="text-center text-gray-500 py-20">
                      Ask me anything about Ayurveda, doshas, diet, lifestyle, or wellness...
                    </div>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] p-4 rounded-xl ${
                          msg.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-muted p-4 rounded-xl">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <form onSubmit={handleChatSend} className="flex space-x-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Type your question..."
                    disabled={loading}
                    data-testid="chat-input"
                  />
                  <Button type="submit" disabled={loading} data-testid="chat-send-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Symptom Checker */}
          <TabsContent value="symptoms">
            <Card className="max-w-4xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Activity className="h-6 w-6 text-primary" />
                  <span>AI Symptom Analysis</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSymptomCheck} className="space-y-4">
                  <textarea
                    value={symptomInput}
                    onChange={(e) => setSymptomInput(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg p-4 min-h-[150px]"
                    placeholder="Describe your symptoms in detail...\nExample: I've been feeling tired, having irregular digestion, and trouble sleeping."
                    data-testid="symptom-input"
                  />
                  <Button type="submit" disabled={loading} data-testid="symptom-check-btn">
                    {loading ? 'Analyzing...' : 'Analyze Symptoms'}
                  </Button>
                </form>

                {symptomResult && (
                  <div className="mt-8 space-y-4">
                    <div className="bg-primary/5 p-6 rounded-lg">
                      <h3 className="font-semibold text-lg mb-2">Analysis Results</h3>
                      <div className="space-y-3">
                        <div>
                          <span className="font-medium">Dosha Imbalance:</span>
                          <span className="ml-2 capitalize text-primary font-semibold">
                            {symptomResult.dosha_imbalance}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Severity:</span>
                          <span className="ml-2 capitalize">{symptomResult.severity}</span>
                        </div>
                        <div>
                          <span className="font-medium">Explanation:</span>
                          <p className="mt-1 text-gray-700">{symptomResult.explanation}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-muted p-6 rounded-lg">
                      <h3 className="font-semibold mb-3">Recommendations:</h3>
                      <ul className="space-y-2">
                        {symptomResult.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start">
                            <span className="mr-2">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {symptomResult.consult_doctor && (
                      <div className="bg-secondary/10 border border-secondary p-4 rounded-lg">
                        <p className="font-medium text-secondary">
                          ⚠️ We recommend consulting with a doctor for proper diagnosis and treatment.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Progress Tracker */}
          <TabsContent value="progress">
            <Card className="max-w-4xl mx-auto">
              <CardHeader>
                <CardTitle>Your Wellness Journey</CardTitle>
              </CardHeader>
              <CardContent>
                {progressAnalysis ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-primary/5 p-6 rounded-lg text-center">
                        <div className="text-3xl font-bold text-primary mb-2">
                          {progressAnalysis.days_since_assessment}
                        </div>
                        <div className="text-sm text-gray-600">Days Since Assessment</div>
                      </div>
                      <div className="bg-primary/5 p-6 rounded-lg text-center">
                        <div className="text-3xl font-bold text-primary mb-2">
                          {progressAnalysis.total_consultations}
                        </div>
                        <div className="text-sm text-gray-600">Total Consultations</div>
                      </div>
                      <div className="bg-primary/5 p-6 rounded-lg text-center">
                        <div className="text-3xl font-bold text-primary mb-2">
                          {progressAnalysis.engagement_score}%
                        </div>
                        <div className="text-sm text-gray-600">Engagement Score</div>
                      </div>
                    </div>

                    <div className="border rounded-lg p-6">
                      <h3 className="font-semibold mb-4">Log how you feel</h3>
                      <form onSubmit={handleTrackOutcome} className="flex flex-wrap items-end gap-4">
                        <div className="flex-1 min-w-[120px]">
                          <Label htmlFor="outcome-type">Metric</Label>
                          <select
                            id="outcome-type"
                            className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                            value={outcomeForm.outcome_type}
                            onChange={(e) => setOutcomeForm({ ...outcomeForm, outcome_type: e.target.value })}
                          >
                            <option value="sleep">Sleep</option>
                            <option value="energy">Energy</option>
                            <option value="stress">Stress</option>
                            <option value="mood">Mood</option>
                          </select>
                        </div>
                        <div className="w-28">
                          <Label htmlFor="outcome-rating">Rating (1–5)</Label>
                          <select
                            id="outcome-rating"
                            className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                            value={outcomeForm.rating}
                            onChange={(e) => setOutcomeForm({ ...outcomeForm, rating: Number(e.target.value) })}
                          >
                            {[1, 2, 3, 4, 5].map((n) => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex-1 min-w-[160px]">
                          <Label htmlFor="outcome-notes">Notes (optional)</Label>
                          <Input
                            id="outcome-notes"
                            placeholder="e.g. Slept well"
                            value={outcomeForm.notes}
                            onChange={(e) => setOutcomeForm({ ...outcomeForm, notes: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <Button type="submit" disabled={outcomeSubmitting}>
                          {outcomeSubmitting ? 'Saving...' : 'Log'}
                        </Button>
                      </form>
                    </div>

                    {(progressAnalysis.outcomes && progressAnalysis.outcomes.length > 0) && (
                      <div className="border rounded-lg p-6">
                        <h3 className="font-semibold mb-4">Recent outcomes (trend)</h3>
                        <ul className="space-y-2 max-h-48 overflow-y-auto">
                          {progressAnalysis.outcomes.slice(0, 20).map((o, i) => (
                            <li key={i} className="flex justify-between items-center text-sm border-b pb-2 last:border-0">
                              <span className="capitalize">{o.outcome_type}</span>
                              <span className="font-medium">{o.rating}/5</span>
                              <span className="text-gray-500">{o.notes || '—'}</span>
                              <span className="text-gray-400">{o.created_at?.slice(0, 10)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="bg-muted p-6 rounded-lg">
                      <h3 className="font-semibold mb-4">Personalized Recommendations</h3>
                      <ul className="space-y-2">
                        {progressAnalysis.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-center">
                            <span className="mr-2">✔️</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-20 text-gray-500">
                    Loading your progress data...
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default AIAssistantPage;